import type { CSSProperties } from 'react';
import { ColorType, FontWeight } from '@/theme';
import { Temper } from '@/types/frontend-types';

export enum FooterState {
  Default = 'default',
  EmailRevealed = 'email',
  AboutUs = 'about',
}

export enum ContactFormStep {
  BeforeStep = 0,
  NameStep = 1,
  EmailStep = 2,
  MessageStep = 3,
  DoneStep = 4,
  ErrorStep = 5,
}

export interface FooterHeaderModel {
  text: string;
  temper?: Temper;
  color?: ColorType;
  fontWeight: FontWeight;
  fontSize: number;
  opacity: number;
}

interface TextInput {
  text: string;
}

interface EmailInput {
  email: string;
}

interface FooterStateInput {
  footerState: FooterState;
}

interface ContactStepInput {
  contactStep: ContactFormStep;
}

interface ContactCodeFileInput extends ContactStepInput {
  isSubmitting: boolean;
}

interface MobileInput {
  isMobile: boolean;
}

interface VisibilityOpacityInput {
  isVisible: boolean;
  visibleOpacity: number;
}

export interface FooterBackgroundLayout {
  triangleFrameStyle: CSSProperties;
  glowStyle: CSSProperties;
  codeMaskStyle: CSSProperties;
}

enum FooterBackgroundVariant {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

export const CONTACT_STEP_CODE_FILES: Record<ContactFormStep, string> = {
  [ContactFormStep.BeforeStep]: '/mock-code/contact-before.txt',
  [ContactFormStep.NameStep]: '/mock-code/contact-name.txt',
  [ContactFormStep.EmailStep]: '/mock-code/contact-email.txt',
  [ContactFormStep.MessageStep]: '/mock-code/contact-message.txt',
  [ContactFormStep.DoneStep]: '/mock-code/contact-done.txt',
  [ContactFormStep.ErrorStep]: '/mock-code/contact-error.txt',
};

export const CONTACT_LOADING_CODE_FILE = '/mock-code/contact-loading.txt';
export const CONTACT_PROCEED_BUTTON_LABEL = 'Proceed';
export const CONTACT_SUBMIT_BUTTON_LABEL = 'Submit';

const MAX_KEYWORD_COUNT = 5;
const MIN_KEYWORD_LENGTH = 4;

const FOOTER_HEADER_BY_STATE: Record<FooterState, FooterHeaderModel> = {
  [FooterState.EmailRevealed]: {
    text: 'envoy@cabal-ai.io',
    temper: Temper.Good,
    color: undefined,
    fontWeight: FontWeight.SemiBold,
    fontSize: 16,
    opacity: 1,
  },
  [FooterState.AboutUs]: {
    text: 'Classified.',
    temper: Temper.Bad,
    color: undefined,
    fontWeight: FontWeight.SemiBold,
    fontSize: 16,
    opacity: 1,
  },
  [FooterState.Default]: {
    text: 'All rights reserved - CABAL AI',
    temper: undefined,
    color: ColorType.White,
    fontWeight: FontWeight.Light,
    fontSize: 14,
    opacity: 0.5,
  },
};

const CODING_TEMPER_BY_STEP: Record<ContactFormStep, Temper> = {
  [ContactFormStep.BeforeStep]: Temper.Neutral,
  [ContactFormStep.NameStep]: Temper.Neutral,
  [ContactFormStep.EmailStep]: Temper.Neutral,
  [ContactFormStep.MessageStep]: Temper.Neutral,
  [ContactFormStep.DoneStep]: Temper.Good,
  [ContactFormStep.ErrorStep]: Temper.Bad,
};

const FOOTER_BACKGROUND_LAYOUT_BY_VARIANT: Record<FooterBackgroundVariant, FooterBackgroundLayout> = {
  [FooterBackgroundVariant.Desktop]: {
    triangleFrameStyle: {
      position: 'absolute',
      left: -250,
      bottom: 20,
      width: 650,
      height: 500,
      pointerEvents: 'none',
    },
    glowStyle: {
      position: 'absolute',
      left: -510,
      bottom: 110,
      width: 1326,
      height: 200,
      borderRadius: 28.33,
      backgroundImage: 'linear-gradient(85deg, rgba(102, 254, 193, 0) 0%, rgba(102, 254, 193, 1) 42%, rgba(102, 254, 193, 0) 100%)',
      filter: 'blur(25.869707107543945px)',
    },
    codeMaskStyle: {
      position: 'absolute',
      left: -250,
      bottom: 20,
      width: 650,
      height: 500,
      pointerEvents: 'none',
      clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
      WebkitClipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '140px 80px 80px 80px',
    },
  },
  [FooterBackgroundVariant.Mobile]: {
    triangleFrameStyle: {
      position: 'absolute',
      left: -150,
      bottom: 170,
      width: 370,
      height: 310,
      pointerEvents: 'none',
    },
    glowStyle: {
      position: 'absolute',
      left: -390,
      bottom: 230,
      width: 906,
      height: 120,
      borderRadius: 28.33,
      backgroundImage: 'linear-gradient(85deg, rgba(102, 254, 193, 0) 0%, rgba(102, 254, 193, 1) 42%, rgba(102, 254, 193, 0) 100%)',
      filter: 'blur(25.869707107543945px)',
    },
    codeMaskStyle: {
      position: 'absolute',
      left: -150,
      bottom: 170,
      width: 370,
      height: 310,
      pointerEvents: 'none',
      clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
      WebkitClipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 40px 40px 40px',
    },
  },
};

export function messageByteSize({ text }: TextInput): number {
  return new TextEncoder().encode(text).length;
}

export function extractKeywords({ text }: TextInput): string {
  const seenKeywords = new Set<string>();
  return text
    .split(/\s+/)
    .reduce<string[]>((keywords, word) => {
      const normalizedWord = word.toLowerCase();
      if (word.length < MIN_KEYWORD_LENGTH || seenKeywords.has(normalizedWord)) {
        return keywords;
      }
      seenKeywords.add(normalizedWord);
      return [...keywords, word];
    }, [])
    .slice(0, MAX_KEYWORD_COUNT)
    .join(', ');
}

export function isValidEmail({ email }: EmailInput): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function getFooterHeaderModel({ footerState }: FooterStateInput): FooterHeaderModel {
  return FOOTER_HEADER_BY_STATE[footerState];
}

export function getCodingTemper({ contactStep }: ContactStepInput): Temper {
  return CODING_TEMPER_BY_STEP[contactStep];
}

export function getContactCodeFile({ contactStep, isSubmitting }: ContactCodeFileInput): string {
  return isSubmitting ? CONTACT_LOADING_CODE_FILE : CONTACT_STEP_CODE_FILES[contactStep];
}

export function shouldClearCodingOnEnd({ contactStep }: ContactStepInput): boolean {
  return contactStep !== ContactFormStep.ErrorStep;
}

export function getFooterBackgroundLayout({ isMobile }: MobileInput): FooterBackgroundLayout {
  const variant = isMobile ? FooterBackgroundVariant.Mobile : FooterBackgroundVariant.Desktop;
  return FOOTER_BACKGROUND_LAYOUT_BY_VARIANT[variant];
}

export function getVisibleOpacity({
  isVisible,
  visibleOpacity,
}: VisibilityOpacityInput): number {
  return isVisible ? visibleOpacity : 0;
}
